"use client"

import { Loader2 } from "lucide-react"
import { IdentitySigil } from "@/components/ui/identity-sigil"
import { useLeaderboard } from "@/hooks/use-leaderboard"

interface LeaderboardModalProps {
  isOpen: boolean
  onClose: () => void
}

const truncateKey = (key: string) => `${key.slice(0, 6)}…${key.slice(-4)}`

const RANK_COLORS: Record<number, string> = {
  1: "text-amber-400",
  2: "text-gray-300",
  3: "text-orange-400",
}

/**
 * Global leaderboard modal - top scores keyed by wallet identity.
 * Scores are submitted automatically at the end of a run for connected
 * players (best score per identity key).
 */
export const LeaderboardModal = ({ isOpen, onClose }: LeaderboardModalProps) => {
  const { entries, isLoading, error, myRank, identityKey } = useLeaderboard(isOpen)

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[80vh] flex flex-col bg-game-surface-elevated border-2 border-game-border rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h2 className="text-foreground text-xl font-bold">Leaderboard</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-2xl leading-none px-1"
            aria-label="Close leaderboard"
          >
            ×
          </button>
        </div>

        {myRank > 0 && (
          <div className="mx-5 mb-2 text-xs text-muted-foreground bg-black/30 rounded-lg px-3 py-2">
            Your rank: <span className="text-amber-400 font-semibold">#{myRank}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-muted-foreground" size={24} />
            </div>
          ) : error ? (
            <div className="text-red-400 text-sm text-center py-8">{error}</div>
          ) : entries.length === 0 ? (
            <div className="text-muted-foreground text-sm text-center py-8">
              No scores yet - be the first! Connect a wallet and finish a run.
            </div>
          ) : (
            <ol className="space-y-1">
              {entries.map((entry, i) => {
                const rank = i + 1
                const isMe = entry.pubkey === identityKey
                return (
                  <li
                    key={entry.pubkey}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 ${
                      isMe ? "bg-amber-950/40 border border-amber-700/50" : "bg-black/20"
                    }`}
                  >
                    <span className={`w-8 text-right font-bold ${RANK_COLORS[rank] ?? "text-muted-foreground"}`}>
                      {rank}
                    </span>
                    <IdentitySigil value={entry.pubkey} size={24} className="rounded" />
                    <span className="flex-1 font-mono text-sm text-foreground truncate">
                      {truncateKey(entry.pubkey)}
                      {isMe && <span className="ml-2 text-amber-400 text-xs">(you)</span>}
                    </span>
                    <span className="text-foreground font-semibold tabular-nums">
                      {entry.bestScore.toLocaleString()}
                    </span>
                  </li>
                )
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  )
}
